<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useMediaQuery, useStorage } from '@vueuse/core';
import { tooltip as vTooltip } from 'uiv';
import {
  ArchiveIcon,
  ChartPieIcon,
  CircleQuestionMarkIcon,
  CogIcon,
  FileSpreadsheetIcon,
  InboxIcon,
  LayoutDashboardIcon,
  MenuIcon,
  TicketIcon,
  UsersIcon,
  XIcon,
  ChartNoAxesCombinedIcon
} from '@lucide/vue';
import ApplicationLogo from '@/components/ApplicationLogo.vue';
import ApplicationLogoMark from '@/components/ApplicationLogoMark.vue';
import SidebarNavUser from '@/components/SidebarNavUser.vue';
import { buildV2DashboardUrl } from '@/utils/v2-links.js';

const store = useStore();

// --- State ---

const mobileMenuOpen = ref(false);
const isBigScreen = useMediaQuery('(min-width: 768px)');

// --- Store-derived state ---

const can = computed(() => store.getters['auth/can']);
const team = computed(() => store.state.me.team);
const teamType = computed(() => store.state.me.teamType);

// --- Navigation items ---

const menuButtonClass =
  'flex h-8 w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500';
const activeLinkClass = 'bg-neutral-100 font-medium text-neutral-600';
const inactiveLinkClass = 'text-white hover:bg-neutral-100 hover:text-neutral-600';

function sidebarTooltip(text) {
  return {
    text,
    customClass: 'sidebar-nav-tooltip',
  };
}

const dataExplorerTooltip = sidebarTooltip('Data Explorer');
const supportTooltip = sidebarTooltip('Support');

const tours = useStorage('txo:tours', {});

const navItems = {
  org: [
    {
      id: 'navDashboard',
      title: 'Dashboard',
      icon: LayoutDashboardIcon,
      permission: 'read-dashboard',
      href: (teamId) => buildV2DashboardUrl({ teamId }),
      tooltip: sidebarTooltip('Dashboard'),
    },
    {
      id: 'navTravel',
      title: 'Travel',
      icon: TicketIcon,
      permission: 'browse-trips',
      href: (teamId) => `/v2/orgs/${teamId}/travel`,
      tooltip: sidebarTooltip('Travel'),
    },
    {
      id: 'navIntelligence',
      title: 'Spend Intelligence',
      icon: ChartNoAxesCombinedIcon,
      permission: 'browse-trips',
      href: (teamId) => `/v2/orgs/${teamId}/spend`,
      tooltip: sidebarTooltip('Spend Intelligence'),
      tour: 'supplier-tour'
    },
    {
      id: 'navTravelers',
      title: 'People',
      icon: UsersIcon,
      permission: 'browse-members',
      route: 'org.members.index',
      tooltip: sidebarTooltip('People'),
    },
    {
      id: 'navEmails',
      title: 'Emails',
      icon: ArchiveIcon,
      permission: 'browse-emails',
      route: 'org.emails.index',
      tooltip: sidebarTooltip('Emails'),
    },
    {
      id: 'navReports',
      title: 'Reports',
      icon: ChartPieIcon,
      permission: 'browse-reports',
      route: 'org.reports.index',
      tooltip: sidebarTooltip('Reports'),
    },
    {
      id: 'navSettings',
      title: 'Settings',
      icon: CogIcon,
      permission: 'browse-settings',
      route: 'org.settings',
      tooltip: sidebarTooltip('Settings'),
    },
  ],
  dev: [
    {
      id: 'navDashboard',
      title: 'Dashboard',
      icon: LayoutDashboardIcon,
      permission: 'read-dashboard',
      route: 'dev.dashboard.index',
      tooltip: sidebarTooltip('Dashboard'),
    },
    {
      id: 'navEmails',
      title: 'Emails',
      icon: ArchiveIcon,
      permission: 'browse-emails',
      route: 'dev.emails.index',
      tooltip: sidebarTooltip('Emails'),
    },
    {
      id: 'navMailboxes',
      title: 'Mailboxes',
      icon: InboxIcon,
      permission: 'browse-mailboxes',
      route: 'dev.mailboxes.index',
      tooltip: sidebarTooltip('Mailboxes'),
    },
    {
      id: 'navReports',
      title: 'Reports',
      icon: ChartPieIcon,
      permission: 'browse-reports',
      route: 'dev.reports.index',
      tooltip: sidebarTooltip('Reports'),
    },
    {
      id: 'navSettings',
      title: 'Settings',
      icon: CogIcon,
      permission: 'browse-settings',
      route: 'dev.settings',
      tooltip: sidebarTooltip('Settings'),
    },
  ],
};

const visibleNavItems = computed(() => {
  const items = navItems[teamType.value];

  if (!items) {
    return [];
  }

  return items.filter((item) => can.value(item.permission));
});

// --- Display computeds ---

const hasResourceCenter = computed(() => {
  if (!Array.isArray(window.pendo?.guides)) {
    return false;
  }

  const rc = window.pendo.guides.filter((guide) => guide.name === 'Resource Center');

  return Boolean(rc.length);
});

const logo = computed(() => (isBigScreen.value ? ApplicationLogoMark : ApplicationLogo));

// --- Navigation actions ---

/**
 * Don't navigate to the support page if the pendo resource center exists
 * @param {Object} evt - browser event
 */
function handleRCTriggerClick(evt) {
  if (hasResourceCenter.value) {
    evt.preventDefault();
  }
}
</script>

<template>
  <div
    id="sidebar"
    class="z-1000 flex flex-none flex-col justify-between bg-brand-blue-darker px-4 py-2 text-sm leading-normal text-white md:fixed md:inset-y-0 md:left-0 md:w-12 md:px-2 md:pt-8 md:pb-4">
    <header class="whitespace-nowrap">
      <div class="flex items-center justify-between">
        <a
          href="/"
          class="block text-white md:mx-auto">
          <Component
            :is="logo"
            class="h-6 w-auto"
            aria-hidden="true" />
          <span class="sr-only">Traxo</span>
        </a>

        <button
          type="button"
          v-if="!isBigScreen"
          @click="mobileMenuOpen = !mobileMenuOpen"
          class="-mr-2 p-2">
          <MenuIcon
            class="block h-6 w-6"
            v-if="!mobileMenuOpen" />
          <XIcon
            class="block h-6 w-6"
            v-else />
        </button>
      </div>

      <nav
        v-show="mobileMenuOpen || isBigScreen"
        class="mt-6 md:mt-14">
        <div v-if="team.id">
          <div
            v-if="team.name"
            :class="[
              { 'h-8': !isBigScreen },
              'flex items-center px-2 text-xs font-semibold text-white/70 md:-mt-8 md:opacity-0',
            ]">
            <span class="truncate">{{ team.name }}</span>
          </div>

          <ul class="flex flex-col gap-1">
            <li
              v-for="item in visibleNavItems"
              :key="item.id">
              <RouterLink
                v-if="item.route"
                :to="{ name: item.route, params: { teamId: team.id } }"
                custom
                v-slot="{ href, navigate, isActive }">
                <a
                  :id="item.id"
                  :href="href"
                  @click="navigate"
                  v-tooltip.right="isBigScreen ? item.tooltip : undefined"
                  :class="[menuButtonClass, isActive ? activeLinkClass : inactiveLinkClass]"
                  v-bind="item.tour && !tours[item.tour] ? { 'data-tour-badge': item.tour, 'data-tour-pulse': true } : {}">
                  <Component
                    :is="item.icon"
                    class="size-4 shrink-0" />
                  <span class="truncate">{{ item.title }}</span>
                </a>
              </RouterLink>

              <a
                v-else
                :id="item.id"
                :href="item.href(team.id)"
                v-tooltip.right="isBigScreen ? item.tooltip : undefined"
                :class="[menuButtonClass, inactiveLinkClass]"
                v-bind="item.tour && !tours[item.tour] ? { 'data-tour-badge': item.tour, 'data-tour-pulse': true } : {}">
                <Component
                  :is="item.icon"
                  class="size-4 shrink-0" />
                <span class="truncate">{{ item.title }}</span>
              </a>
            </li>
          </ul>
        </div>
      </nav>
    </header>

    <footer
      v-show="mobileMenuOpen || isBigScreen"
      class="mt-2 flex flex-col gap-2 border-t border-white/20 pt-2 md:mt-0 md:border-t-0 md:pt-0">
      <ul class="flex flex-col gap-1">
        <li v-if="can('add-downloads') && team.id && teamType === 'org'">
          <RouterLink
            :to="{ name: 'org.resources.index', params: { teamId: team.id } }"
            custom
            v-slot="{ href, navigate, isActive }">
            <a
              id="navResources"
              :href="href"
              @click="navigate"
              v-tooltip.right="isBigScreen ? dataExplorerTooltip : undefined"
              :class="[menuButtonClass, isActive ? activeLinkClass : inactiveLinkClass]">
              <FileSpreadsheetIcon class="size-4 shrink-0" />
              <span class="truncate">Data Explorer</span>
            </a>
          </RouterLink>
        </li>

        <li>
          <a
            href="/support"
            @click="handleRCTriggerClick"
            id="resource-center"
            v-tooltip.right="isBigScreen ? supportTooltip : undefined"
            :class="[menuButtonClass, inactiveLinkClass]">
            <CircleQuestionMarkIcon class="size-4 shrink-0" />
            <span class="truncate">Support</span>
          </a>
        </li>
      </ul>

      <SidebarNavUser :is-big-screen="isBigScreen" />
    </footer>
  </div>
</template>
